// src/services/venueApi.js
import { useQuery } from '@tanstack/react-query';
import { REGION_CONFIG, getApiBaseUrl } from '../config/region';

// ✅ FIX: Use dynamic API URL from config
const API_BASE_URL = getApiBaseUrl();

// Request queue to prevent duplicate simultaneous requests
const requestQueue = new Map();
const abortControllers = new Map();

// Helper to create unique request key
const createRequestKey = (endpoint, params) => {
  return `${endpoint}_${JSON.stringify(params)}`;
};

// ✅ FIXED: Get auth token from correct location
const getAuthToken = () => {
  return localStorage.getItem('token') || localStorage.getItem('venue_app_token');
};

// Helper to fetch with timeout and abort capability
const fetchWithTimeout = async (url, options = {}, timeout = 15000) => {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeout);
  
  try {
    const token = getAuthToken();
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };
    
    // ✅ FIXED: Only add Authorization header if token exists and is non-empty
    if (token && token.trim() !== '') {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
      headers,
    });
    clearTimeout(id);
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    return response;
  } catch (error) {
    clearTimeout(id);
    
    if (error.name === 'AbortError') {
      throw new Error('Request timeout');
    }
    
    throw error;
  }
};

// Optimized fetch function with request deduplication
const optimizedFetch = async (endpoint, params = {}) => {
  const requestKey = createRequestKey(endpoint, params);
  
  // Check if same request is already in progress
  if (requestQueue.has(requestKey)) {
    console.log('📦 Reusing pending request:', endpoint);
    return requestQueue.get(requestKey);
  }
  
  // Cancel any previous request for this endpoint
  if (abortControllers.has(endpoint)) {
    abortControllers.get(endpoint).abort();
  }
  
  const controller = new AbortController();
  abortControllers.set(endpoint, controller);
  
  // Create the promise
  const requestPromise = (async () => {
    try {
      const queryParams = new URLSearchParams();
      
      // ✅ Always include region parameter
      queryParams.append('region', params.region || REGION_CONFIG.region);
      queryParams.append('page', params.page || 1);
      queryParams.append('limit', params.limit || 1000);
      
      // Add other params
      Object.keys(params).forEach(key => {
        if (key !== 'region' && key !== 'page' && key !== 'limit' && 
            params[key] !== undefined && params[key] !== null && params[key] !== '') {
          queryParams.append(key, params[key]);
        }
      });
      
      const url = `${API_BASE_URL}${endpoint}?${queryParams.toString()}`;
      
      const token = getAuthToken();
      console.log('🔄 Fetching from:', url);
      console.log('🌍 Region:', params.region || REGION_CONFIG.region);
      console.log('🔐 Auth token:', token ? 'Present' : 'Missing');
      
      const response = await fetchWithTimeout(
        url,
        { signal: controller.signal },
        15000
      );
      
      const result = await response.json();
      
      // ✅ FIX: Handle production API response format {success: true, data: [...], region: "th"}
      const data = result.data || result.venues || result;
      
      console.log('✅ Data loaded:', Array.isArray(data) ? `${data.length} items` : 'N/A');
      
      return data;
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('🔄 Request cancelled:', endpoint);
        return null;
      }
      console.error('❌ Fetch error:', error);
      throw error;
    } finally {
      requestQueue.delete(requestKey);
      abortControllers.delete(endpoint);
    }
  })();
  
  requestQueue.set(requestKey, requestPromise);
  
  return requestPromise;
};

// ⭐ React Query-compatible fetch functions

// ✅ FIXED: Fetch all venues - use /venues endpoint for all regions
export const fetchAllVenues = async (params = {}) => {
  const defaultParams = {
    page: params.page || 1,
    limit: params.limit || 1000,
    region: params.region || REGION_CONFIG.region,
    ...params
  };
  
  // ✅ CRITICAL FIX: Use /venues endpoint (same as CategoryCarousel)
  const endpoint = '/venues';
  
  console.log('🏢 Fetching venues for region:', defaultParams.region, 'from endpoint:', endpoint);
  
  const result = await optimizedFetch(endpoint, defaultParams);
  
  if (!result) {
    throw new Error('Failed to fetch venues');
  }
  
  return Array.isArray(result) ? result : [];
};

// Fetch single venue by ID
export const fetchVenueById = async (venueId, allVenues) => {
  if (!venueId) {
    throw new Error('Venue ID is required');
  }
  
  const venue = allVenues.find(v => 
    v._id === venueId || 
    v.Dubai_id === venueId ||
    v._id === `venue-${venueId}` ||
    v.Dubai_id === `venue-${venueId}`
  );
  
  if (!venue) {
    throw new Error('Venue not found');
  }
  
  return venue;
};

// Search venues
export const searchVenues = (venues, searchTerm) => {
  if (!searchTerm || searchTerm.trim() === '') {
    return venues;
  }
  
  const lowerSearchTerm = searchTerm.toLowerCase();
  
  return venues.filter(venue =>
    venue.name?.toLowerCase().includes(lowerSearchTerm) ||
    venue.city?.toLowerCase().includes(lowerSearchTerm) ||
    venue.venue_category_display?.toLowerCase().includes(lowerSearchTerm) ||
    venue.district?.toLowerCase().includes(lowerSearchTerm)
  );
};

// Get venues by category
export const getVenuesByCategory = (venues, category) => {
  if (!category) {
    throw new Error('Category is required');
  }
  
  return venues.filter(venue => venue.venue_category_display === category);
};

// Get nearby venues
export const getNearbyVenues = (venues, lat, lng, radius = 5) => {
  if (!lat || !lng) {
    throw new Error('Latitude and longitude are required');
  }
  
  const toRadians = (degrees) => degrees * (Math.PI / 180);
  
  return venues.filter(venue => {
    const venueLat = venue.lat;
    const venueLng = venue.lng;
    
    if (!venueLat || !venueLng) return false;
    
    const R = 6371;
    const dLat = toRadians(venueLat - lat);
    const dLng = toRadians(venueLng - lng);
    
    const a = 
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRadians(lat)) * Math.cos(toRadians(venueLat)) *
      Math.sin(dLng / 2) * Math.sin(dLng / 2);
    
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c;
    
    return distance <= radius;
  });
};

// Get filter options
export const getFilterOptions = (venues) => {
  const venueTypes = [...new Set(
    venues.map(v => v.venue_type_display).filter(Boolean)
  )].sort();
  
  const venueCategories = [...new Set(
    venues.map(v => v.venue_category_display).filter(Boolean)
  )].sort();
  
  const districts = [...new Set(
    venues.map(v => v.district).filter(Boolean)
  )].sort();

  return {
    venueTypes,
    venueCategories,
    districts
  };
};

// Filter venues with criteria
export const getFilteredVenues = (venues, filters = {}) => {
  return venues.filter(venue => {
    if (filters.venueType && venue.venue_type_display !== filters.venueType) {
      return false;
    }
    
    if (filters.category && venue.venue_category_display !== filters.category) {
      return false;
    }
    
    if (filters.district && venue.district !== filters.district) {
      return false;
    }
    
    return true;
  });
};

// ⭐ React Query hooks

// Hook for fetching all venues
export const useVenues = (params = {}) => {
  return useQuery({
    queryKey: ['venues', REGION_CONFIG.region, params],
    queryFn: () => fetchAllVenues(params),
    staleTime: 10 * 60 * 1000,
    cacheTime: 30 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: 2,
    onError: (error) => {
      console.error('❌ Failed to fetch venues:', error.message);
      if (error.message.includes('401') || error.message.includes('Unauthorized')) {
        console.warn('⚠️ Authentication issue - token may be invalid');
      }
    }
  });
};

// Hook for fetching single venue
export const useVenue = (venueId) => {
  const { data: allVenues = [] } = useVenues();
  
  return useQuery({
    queryKey: ['venue', REGION_CONFIG.region, venueId],
    queryFn: () => fetchVenueById(venueId, allVenues),
    staleTime: 10 * 60 * 1000,
    cacheTime: 30 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: !!venueId && allVenues.length > 0,
  });
};

// Hook for searching venues
export const useSearchVenues = (searchTerm) => {
  const { data: allVenues = [] } = useVenues();
  
  return useQuery({
    queryKey: ['search-venues', REGION_CONFIG.region, searchTerm],
    queryFn: () => searchVenues(allVenues, searchTerm),
    staleTime: 5 * 60 * 1000,
    cacheTime: 30 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: allVenues.length > 0,
  });
};

// Hook for venues by category
export const useVenuesByCategory = (category) => {
  const { data: allVenues = [] } = useVenues();
  
  return useQuery({
    queryKey: ['venues-category', REGION_CONFIG.region, category],
    queryFn: () => getVenuesByCategory(allVenues, category),
    staleTime: 10 * 60 * 1000,
    cacheTime: 30 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: !!category && allVenues.length > 0,
  });
};

// Hook for nearby venues
export const useNearbyVenues = (lat, lng, radius = 5) => {
  const { data: allVenues = [] } = useVenues();
  
  return useQuery({
    queryKey: ['nearby-venues', REGION_CONFIG.region, lat, lng, radius],
    queryFn: () => getNearbyVenues(allVenues, lat, lng, radius),
    staleTime: 5 * 60 * 1000,
    cacheTime: 30 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: !!lat && !!lng && allVenues.length > 0,
  });
};

// Hook for filter options
export const useFilterOptions = () => {
  const { data: allVenues = [] } = useVenues();
  
  return useQuery({
    queryKey: ['filter-options', REGION_CONFIG.region],
    queryFn: () => getFilterOptions(allVenues),
    staleTime: 10 * 60 * 1000,
    cacheTime: 30 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: allVenues.length > 0,
  });
};

// Hook for filtered venues
export const useFilteredVenues = (filters = {}) => {
  const { data: allVenues = [] } = useVenues();
  
  return useQuery({
    queryKey: ['filtered-venues', REGION_CONFIG.region, filters],
    queryFn: () => getFilteredVenues(allVenues, filters),
    staleTime: 5 * 60 * 1000,
    cacheTime: 30 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: allVenues.length > 0,
  });
};

// ⭐ Legacy service object (backwards compatible)
export const venueService = {
  async getVenues(params = {}) {
    try {
      return await fetchAllVenues(params);
    } catch (error) {
      console.error('Error fetching venues:', error);
      throw new Error(error.message || 'Failed to fetch venues');
    }
  },

  async getVenueById(venueId) {
    try {
      const venues = await this.getVenues();
      return await fetchVenueById(venueId, venues);
    } catch (error) {
      console.error('Error fetching venue:', error);
      throw new Error(error.message || 'Failed to fetch venue details');
    }
  },

  async searchVenues(searchTerm) {
    try {
      const venues = await this.getVenues();
      return searchVenues(venues, searchTerm);
    } catch (error) {
      console.error('Error searching venues:', error);
      throw new Error(error.message || 'Search failed');
    }
  },

  async getVenuesByCategory(category) {
    try {
      const venues = await this.getVenues();
      return getVenuesByCategory(venues, category);
    } catch (error) {
      console.error('Error fetching venues by category:', error);
      throw new Error(error.message || 'Failed to fetch venues by category');
    }
  },

  async getNearbyVenues(lat, lng, radius = 5) {
    try {
      const venues = await this.getVenues();
      return getNearbyVenues(venues, lat, lng, radius);
    } catch (error) {
      console.error('Error fetching nearby venues:', error);
      throw new Error(error.message || 'Failed to fetch nearby venues');
    }
  },

  async getFilterOptions() {
    try {
      const venues = await this.getVenues();
      return getFilterOptions(venues);
    } catch (error) {
      console.error('Error fetching filter options:', error);
      throw new Error(error.message || 'Failed to fetch filter options');
    }
  },

  async getFilteredVenues(filters = {}) {
    try {
      const venues = await this.getVenues();
      return getFilteredVenues(venues, filters);
    } catch (error) {
      console.error('Error filtering venues:', error);
      throw new Error(error.message || 'Failed to filter venues');
    }
  },

  cancelAllRequests: () => {
    abortControllers.forEach(controller => {
      controller.abort();
    });
    abortControllers.clear();
    requestQueue.clear();
    console.log('All venue requests cancelled');
  },

  async prefetchVenues() {
    try {
      await this.getVenues();
      console.log('Venues prefetched successfully for region:', REGION_CONFIG.region);
    } catch (error) {
      console.error('Error prefetching venues:', error);
    }
  }
};

export default venueService;